// src/modules/estimates/EstimateForm.js
import React, { useState, useEffect, navigate } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import Checkbox from '../../components/shared/Checkbox';
import GooglePlaceAutocomplete from '../../components/shared/GooglePlaceAutocomplete';
import Button from '../../components/shared/Button';
import Stepper from '../../components/shared/Stepper';
import Map from '../../components/shared/Map';
import TextArea from '../../components/shared/TextArea';
import { getLeadById, searchLeads,getLeadStatuses, getLeadOrigins } from '../leads/leadService';
import { getLeadAddresses, createLeadAddress } from '../leads/leadAddressService';
import { getServices, getSuppliersByLocation, createEstimate, getEstimateById, updateEstimate  } from './EstimateService'; // Assuming estimateService exists for details and services
import { getEstimateDetails } from './EstimateDetailService';
import { generateIntroduction as aiGenerateIntroduction, generateFootnotes as aiGenerateFootnotes } from './aiEstimateService';
import { getUnitInfo } from './ServiceModal';
import { calculateDistance } from '../../utils/distanceUtils';
import ServiceModal from './ServiceModal';
import { FaArrowDown, FaArrowLeft, FaArrowRight, FaCalendar, FaCheck, FaChevronDown, FaChevronRight, FaCloudUploadAlt, FaEdit, FaEnvelope, FaMagic, FaMap, FaMapMarkerAlt, FaPercent, FaPhone, FaPlus, FaRegArrowAltCircleRight, FaRegHdd, FaSearch, FaTextHeight, FaTimes, FaTools, FaTractor, FaTrash, FaUser } from 'react-icons/fa';
import MaskedInput from '../../components/shared/MaskedInput';
import Autocomplete from '../../components/shared/Autocomplete'; // Assuming you have an Autocomplete component
import { BackButton, FormContainer, FormField, FormGrid, FormHeader, FormSection } from '../../components/styled/FormComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { ActionButton } from '../../components/styled/CommonStyles';
import { DetailField, DetailGrid, DetailGridSeparator } from '../../components/styled/DetailComponents';
import { formatCurrency } from '../../utils/utils';
import FeedbackModal from '../../components/shared/ModalNotification';
import DiscountInput from '../../components/shared/DiscountInput';
import { calcDiscount, discountPayload, discountFormValue } from '../../utils/discount';
import EstimateImageUpload from './EstimateImageUpload';

// Mirrors ESTIMATE_VALIDITY_DAYS in the backend's helpers/estimateHelpers.js.
const ESTIMATE_VALIDITY_DAYS = 180;
const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
`;

const ServiceItem = styled.div`
  margin-bottom: ${(props) => props.theme.spacing(3)};
  padding: ${(props) => props.theme.spacing(2)};
  background-color: ${(props) => props.theme.colors.background};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom:2px;
    font-weight: bold;
  }

  .content {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  .valueTotal{
    font-size: 1.2em;
    font-weight: bold;
    text-align: right;
    padding: 10px 15px;
  }
`;

// Additional styles if needed
const MapWrapper = styled.div`
    width: 100%;
    height: 400px;
    margin-top: ${(props) => props.theme.spacing(4)};
`;

const SupplierItem = styled.div`
  display: flex;
  flex-direction: column; /* Stack items vertically */
  padding: 10px 10px;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray};
  margin-bottom: ${(props) => props.theme.spacing(2)};

  .header {
    display: flex;
    justify-content: space-between; /* Add space between supplier name and distance */
    width: 100%; /* Ensure the header takes full width */
    align-items: center;
  }

  .supplier-name {
    font-size: 18px;
    font-weight: bold;
  }

  .distance {
    font-size: 16px;
    font-weight: bold;
    color: ${(props) => props.theme.colors.primaryText};
  }

  .categories-list {
    margin-top: ${(props) => props.theme.spacing(1)};
    font-size: 14px;
    color: ${(props) => props.theme.colors.textSecondary};
  }
`;

const ActionIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: ${(props) => props.theme.spacing(2)};
  svg {
    cursor: pointer;
    &:hover {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`;

const DiscountSection = styled.div`
  margin-top: ${(props) => props.theme.spacing(4)};
  padding: ${(props) => props.theme.spacing(2)};
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(3)};
`;

const DiscountLabel = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: ${(props) => props.theme.spacing(1)};
`;

const DiscountInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${(props) => props.theme.spacing(3)};
  align-items: center;
`;

// Additional styled-components for Step 3
const SummaryWrapper = styled.div`
  margin-top: ${(props) => props.theme.spacing(4)};
  padding: ${(props) => props.theme.spacing(4)};
  background-color: ${(props) => props.theme.colors.surface};
  border-radius: ${(props) => props.theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const SummaryTitle = styled.h3`
  font-size: 20px;
  color: ${(props) => props.theme.colors.primaryText};
  margin-bottom: ${(props) => props.theme.spacing(3)};
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(3)};
`;

const SupplierListWrapper = styled.div`
padding: 20px;
background-color: ${(props) => props.theme.colors.surface};
border-radius: 12px;
box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
margin-top: ${(props) => props.theme.spacing(4)};
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: ${(props) => props.theme.spacing(2)} 0;

  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: ${(props) => props.theme.spacing(1)};
  }

  svg {
    transition: transform 0.2s ease;
  }
`;

const AccordionContent = styled.div`
  overflow: hidden;
  max-height: ${(props) => (props.$isOpen ? '2000px' : '0')};
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  transition: max-height 0.3s ease, opacity 0.3s ease;
`;

const AddressOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(1.5)};
`;

const AddressOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1.5)};
  padding: ${(props) => props.theme.spacing(2)};
  border-radius: ${(props) => props.theme.borderRadius};
  border: 1px solid ${(props) => props.$active ? props.theme.colors.primary : props.theme.colors.inputGray + '44'};
  background: ${(props) => props.$active ? props.theme.colors.primary + '11' : props.theme.colors.background};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    background: ${(props) => props.theme.colors.primary + '0a'};
  }

  input[type="radio"] {
    accent-color: ${(props) => props.theme.colors.primary};
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .addr-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .addr-label {
    font-weight: ${(props) => props.theme.typography.fontWeight.medium};
    color: ${(props) => props.theme.colors.textPrimary};
    font-size: ${(props) => props.theme.typography.fontSize.medium};
  }

  .addr-detail {
    color: ${(props) => props.theme.colors.textSecondary};
    font-size: ${(props) => props.theme.typography.fontSize.small};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const AddNewAddrButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing(1)};
  padding: ${(props) => props.theme.spacing(1.5)} ${(props) => props.theme.spacing(2)};
  border: 1px dashed ${(props) => props.theme.colors.inputGray};
  border-radius: ${(props) => props.theme.borderRadius};
  background: transparent;
  color: ${(props) => props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: ${(props) => props.theme.typography.fontSize.small};
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    color: ${(props) => props.theme.colors.primary};
  }
`;

const NewAddressForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing(1.5)};
  padding: ${(props) => props.theme.spacing(2)};
  border: 1px solid ${(props) => props.theme.colors.inputGray + '44'};
  border-radius: ${(props) => props.theme.borderRadius};
  background: ${(props) => props.theme.colors.background};
`;

  // Assuming you pass `suppliers` as a prop to this component
  const SupplierListComponent  = ({ suppliers, title, icon }) => {
    return (
      <SupplierListWrapper>
        <SummaryTitle>{icon} {title}</SummaryTitle>
        {suppliers && suppliers.length > 0 ? (
          suppliers.map((supplier) => (
            <SupplierItem key={supplier.id}>
                <div className="header">
                    <span className="supplier-name">{supplier.name}</span>
                    <span className="distance">{supplier.distance} miles</span>
                </div>
                <div className="categories-list">
                    {supplier.categories && supplier.categories.length > 0 ? (
                        supplier.categories.map((category, index) => (
                        <span key={index}>{category.name}{index < supplier.categories.length - 1 ? ', ' : ''}</span>
                        ))
                    ) : (
                        <span>No categories available</span>
                    )}
                    </div>

            </SupplierItem>
          ))
        ) : (
          <div>No suppliers found within the specified radius.</div>
        )}
      </SupplierListWrapper>
    );
  };

const EstimateForm = () => {
  const { leadId, estimateId } = useParams(); // Check if there's a lead ID in the route params
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const serviceRequestId = searchParams.get('serviceRequestIds');

  const [step, setStep] = useState(1); // Step tracking state
  const [formData, setFormData] = useState({
    lead: {
      id: leadId || '',
      name: '',
      email: '',
      phone: '',
      street: '',
      city: '',
      state: '',
      zipcode: '',
      latitude: '',
      longitude: '',
      accept_sms: false,
      when_ready: '',
      job_observation: '',  // Added field for job observation
      lead_origin: '' // Added field for lead origin
    },
    services: [],
    details: [],
    mapSuppliers: [],
    estimateData: {},
    EstimateDetailId: [],
    introduction: '',
    notes: '',
  });
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [leadOrigins, setLeadOrigins] = useState([]);
  const [availableLeads, setAvailableLeads] = useState([])

  const [availableDetails, setAvailableDetails] = useState([]); // All predefined details for estimates
  const [availableServices, setAvailableServices] = useState([]); // List of services for selection
  const [loading, setLoading] = useState(false);
  const [selectedServiceIndex, setSelectedServiceIndex] = useState(null); // To know which service's area is being measured
  const [suppliers, setSuppliers] = useState([]);
  const [serviceModalOpen, setServiceModalOpen] = useState(false); // State for ServiceModal visibility
  const [totalValue, setTotalValue] = useState(0);
  const [discountedTotal, setDiscountedTotal] = useState(0);
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' | 'fixed'
  const [discountValue, setDiscountValue] = useState(''); // % or $ depending on discountType
  const [discountMotive, setDiscountMotive] = useState(''); // Discount motive state
  const [leadFieldsDisabled, setLeadFieldsDisabled] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [generatingIntro, setGeneratingIntro] = useState(false);
  const [generatingFootnotes, setGeneratingFootnotes] = useState(false);
  const [aiPromptModal, setAiPromptModal] = useState({ open: false, type: null }); // type: 'introduction' | 'footnotes'
  const [aiCustomInstructions, setAiCustomInstructions] = useState('');

  // Multi-address state
  const [addressMode, setAddressMode] = useState('main'); // 'main' | 'different'
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [leadAddresses, setLeadAddresses] = useState([]);
  const [newAddressMode, setNewAddressMode] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: '', street: '', city: '', state: '', zipcode: '', latitude: null, longitude: null });

  const navigate = useNavigate();

  const supplierPinUrl = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png'; // Blue pin for suppliers
  const jobSitePinUrl = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'; // Red pin for job site


  useEffect(() => {

    console.log("Params: ", serviceRequestId);

    try{
      if(estimateId){
        fetchEstimateDetails(estimateId);
        setLoading(true)
      }else if (leadId) {
        fetchLeadDetails(leadId);
        setLoading(true)
      }else if(serviceRequestId){
        setLoading(true)
        fetchLeadDetails(leadId);
        setFormData({
          ...formData, 
          serviceRequestIds: serviceRequestId
        });
      }
      fetchLeadOrigins();
      fetchEstimateData();
    }catch(error){
      console.error("Error loading Page", error);
    }finally{
      setLoading(false);      
    }
  }, [leadId, estimateId]);

  // Resolve effective address based on address mode
  const getEffectiveAddress = () => {
    if (addressMode === 'different' && selectedAddressId) {
      const addr = leadAddresses.find(a => a.id === selectedAddressId);
      if (addr) return addr;
    }
    return { street: formData.lead.street, city: formData.lead.city, state: formData.lead.state, zipcode: formData.lead.zipcode, latitude: formData.lead.latitude, longitude: formData.lead.longitude };
  };

  const getEffectiveCoords = () => {
    const addr = getEffectiveAddress();
    return { lat: addr.latitude, lng: addr.longitude };
  };

  useEffect(() => {
    const { lat, lng } = getEffectiveCoords();
    if (step === 2 && lat && lng) {
      fetchSuppliers(lat, lng);
    }
  }, [step, formData.lead.latitude, formData.lead.longitude, addressMode, selectedAddressId]);

   // Use useEffect to watch for changes in the `services` array and recalculate the total value
  // Recalculate Total Value whenever services or discount change
  useEffect(() => {
    const totalPrice = formData.services.reduce(
      (acc, service) => acc + (parseFloat(service.totalPrice) || 0),
      0
    );
    setTotalValue(totalPrice);

    setDiscountedTotal(calcDiscount(totalPrice, discountType, discountValue).total);
  }, [formData.services, discountType, discountValue]);

  const discountCalc = calcDiscount(totalValue, discountType, discountValue);

  useEffect(() => {
    if (step === 3) {
      fetchLeadStatuses();
    }
  }, [step]);

  const fetchLeadStatuses = async () => {
    try {
      const response = await getLeadStatuses();
      setLeadStatuses(response.data);
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
    }
  };

  const fetchEstimateDetails = async (id) => {
    setLoading(true);
    try {
      const response = await getEstimateById(id); // Implement getEstimateById in EstimateService
      if (response.data) {
        const estimateData = response.data;

        // Map through the services and make sure each service has a `serviceName` field.
      const servicesWithNames = estimateData.items.map((service) => ({
        ...service,
        serviceName: service.Service ? service.Service.service_name : '', // Use `service.Service.service_name` if it exists.
      }));

        setFormData({
          ...formData,
          lead: {
            ...formData.lead,
            ...estimateData.Lead,
            lead_origin: estimateData.Lead.origin?.origin_name || '',
            job_observation: estimateData.Lead.job_observation || '',
          },
          services: servicesWithNames,
          EstimateDetailId: estimateData.details,
          estimateData: {
            ...estimateData
          },
          totalPrice: estimateData.totalPrice,
          discountPercentage: estimateData.discountPercentage,
          discountReason: estimateData.discountReason,
          status: estimateData.status,
          notes: estimateData.notes,
          introduction: estimateData.introduction,
        });

        // Pre-populate address mode for existing estimates
        if (estimateData.leadAddressId && estimateData.leadAddress) {
          setAddressMode('different');
          setSelectedAddressId(estimateData.leadAddressId);
        }
        // Fetch lead addresses for the lead
        if (estimateData.Lead?.id) {
          try {
            const addrResponse = await getLeadAddresses(estimateData.Lead.id);
            setLeadAddresses(addrResponse.data || []);
          } catch (err) {
            console.error('Error fetching lead addresses:', err);
          }
          setLeadFieldsDisabled(true);
        }

        const savedDiscount = discountFormValue(estimateData);
        setDiscountType(savedDiscount.type);
        setDiscountValue(savedDiscount.value);
        setDiscountMotive(estimateData.discountReason || '');

      }
    } catch (error) {
      console.error('Error fetching estimate details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadOrigins = async () => {
    try {
      const response = await getLeadOrigins(); // Fetch lead origins
      setLeadOrigins(response.data);
    } catch (error) {
      console.error('Error fetching lead origins:', error);
    }
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  // Switching % <-> $ clears the value; 10 (%) and 10 ($) mean different things
  const handleDiscountTypeChange = (type) => {
    if (type === discountType) return;
    setDiscountType(type);
    setDiscountValue('');
  };

  // Handle Discount Motive Change
  const handleDiscountMotiveChange = (e) => {
    setDiscountMotive(e.target.value);
  };

  // Fetch lead details if an existing lead is being used
  const fetchLeadDetails = async (id) => {
    setLoading(true);
    try {
      const response = await getLeadById(id);
      if (response.data) {
        setFormData({
          ...formData,
          lead: {
            ...formData.lead,
            ...response.data
          }
        });
        setLeadFieldsDisabled(true);
        // Fetch lead addresses
        try {
          const addrResponse = await getLeadAddresses(id);
          setLeadAddresses(addrResponse.data || []);
        } catch (err) {
          console.error('Error fetching lead addresses:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
    } finally {
    setLoading(false);
    }
  };
  // Handle input change for lead form fields
  const handleLeadChange = (e) => {
    console.log(e.target);
    const { name, value } = e.target;
    setFormData({
      ...formData,
      lead: {
        ...formData.lead,
        [name]: value
      }
    });
  };

  // Handle lead name search for autocomplete
  const handleLeadNameSearch = async (query) => {
    if (!query) return;
    try {
      const response = await searchLeads(query);
      setAvailableLeads(response.data);
    } catch (error) {
      console.error('Error searching leads:', error);
    }
  };

 // Handle lead selection from autocomplete
const handleLeadSelect = async (selectedLead) => {
    if (selectedLead === 'add_new_lead') {
      // Enable lead input fields for new lead
      setLeadFieldsDisabled(false);
      setAddressMode('main');
      setSelectedAddressId(null);
      setLeadAddresses([]);
      setNewAddressMode(false);
      setFormData({
        ...formData,
        lead: {
          ...formData.lead,
          name: '',
          email: '',
          phone: '',
          street: '',
          city: '',
          state: '',
          zipcode: '',
          latitude: '',
          longitude: '',
          accept_sms: '',
          when_ready: '',
          job_observation: '',
          lead_origin: ''
        },
      });
    } else {
      // Populate formData with the selected lead's data
      setFormData({
        ...formData,
        lead: {
          ...formData.lead,
          ...selectedLead,
          lead_origin: selectedLead.origin.origin_name,
        },
      });
      // Disable lead input fields
      setLeadFieldsDisabled(true);
      // Reset address selection
      setAddressMode('main');
      setSelectedAddressId(null);
      setNewAddressMode(false);
      // Fetch lead addresses
      try {
        const response = await getLeadAddresses(selectedLead.id);
        setLeadAddresses(response.data || []);
      } catch (error) {
        console.error('Error fetching lead addresses:', error);
        setLeadAddresses([]);
      }
    }
  };

   // Fetch services and details for estimates
   const fetchEstimateData = async () => {
    setLoading(true);
    try {
      const [detailsResponse, servicesResponse] = await Promise.all([
        getEstimateDetails(),
        getServices()
      ]);
      setAvailableDetails(detailsResponse);
      setAvailableServices(servicesResponse.data);
    } catch (error) {
      console.error('Error fetching estimate data:', error);
    }
    setLoading(false);
  };

  const fetchSuppliers = async (lat, lng) => {
    try {
      // Fetch suppliers from the API based on location
      const response = await getSuppliersByLocation(lat, lng, 20);
      const fetchedSuppliers = response.data;

      const suppliersWithCategories = fetchedSuppliers.map((supplier) => ({
        ...supplier,
        distance: calculateDistance(
          lat,
          lng,
          supplier.latitude,
          supplier.longitude
        ).toFixed(2),
        // Ensure categories are properly formatted
        categories: supplier.categories || [], // Default to an empty array if undefined
      }));

      // Sort suppliers by distance (closest to farthest)
      const sortedSuppliers = suppliersWithCategories.sort(
        (a, b) => a.distance - b.distance
      );

      // Update the state with sorted suppliers
      setSuppliers(sortedSuppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const effectiveCoords = getEffectiveCoords();
  const formattedSuppliers = suppliers.map((supplier) => {
    const productNames = Array.isArray(supplier.products)
      ? supplier.products.map((product) => product.name)
      : [];

    return {
      lat: supplier.latitude,
      lng: supplier.longitude,
      title: supplier.name,
      distance: calculateDistance(
        effectiveCoords.lat,
        effectiveCoords.lng,
        supplier.latitude,
        supplier.longitude
      ),
      products: productNames,
      isSupplier: true,
    };
  });

  const handleDetailChange = (detailId) => {
    setFormData(prevFormData => {
      const currentDetails = prevFormData.EstimateDetailId || [];
      const isDetailIncluded = currentDetails.some(item => item.id === detailId);

      let updatedDetails;
      if (isDetailIncluded) {
        // Remove the detail if it's already included
        updatedDetails = currentDetails.filter(item => item.id !== detailId);
      } else {
        // Add the detail if it's not already included
        updatedDetails = [...currentDetails, { id: detailId }];
      }

      return {
        ...prevFormData,
        EstimateDetailId: updatedDetails
      };
    });
  };

  const handleAddService = () => {
    setSelectedServiceIndex(null);
    setServiceModalOpen(true);
  };

  const handleServiceSave = (serviceData) => {
    const updatedServices = [...formData.services];
    if (selectedServiceIndex !== null) {
      updatedServices[selectedServiceIndex] = serviceData;
    } else {
      updatedServices.push(serviceData);
    }
    setFormData({ ...formData, services: updatedServices });
    setServiceModalOpen(false);
  };

  const handleServiceEdit = (index) => {
    setSelectedServiceIndex(index);
    setServiceModalOpen(true);
  };

  const handleServiceDelete = (index) => {
    const updatedServices = formData.services.filter((_, i) => i !== index);
    setFormData({ ...formData, services: updatedServices });
  };

  // Handle Google autocomplete selection
  const handleAddressSelect = (address, lat, lng, place) => {
    setFormData((prevData) => ({
      ...prevData,
      lead: {
        ...prevData.lead,
        street: address,
        city: place.address_components.find((c) => c.types.includes('locality'))?.long_name || prevData.lead.city,
        state: place.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || prevData.lead.state,
        zipcode: place.address_components.find((c) => c.types.includes('postal_code'))?.long_name || prevData.lead.zipcode,
        latitude: lat,
        longitude: lng,
      }
    }));
    fetchSuppliers(lat, lng);
  };

  // Handle new address selection from GooglePlaceAutocomplete
  const handleNewAddressSelect = (address, lat, lng, place) => {
    setNewAddress({
      ...newAddress,
      street: address,
      city: place.address_components.find((c) => c.types.includes('locality'))?.long_name || '',
      state: place.address_components.find((c) => c.types.includes('administrative_area_level_1'))?.short_name || '',
      zipcode: place.address_components.find((c) => c.types.includes('postal_code'))?.long_name || '',
      latitude: lat,
      longitude: lng,
    });
  };

  // Save new address and select it
  const handleSaveNewAddress = async () => {
    if (!newAddress.street || !formData.lead.id) return;
    try {
      const response = await createLeadAddress(formData.lead.id, newAddress);
      const created = response.data;
      setLeadAddresses(prev => [...prev, created]);
      setSelectedAddressId(created.id);
      setNewAddressMode(false);
      setNewAddress({ label: '', street: '', city: '', state: '', zipcode: '', latitude: null, longitude: null });
    } catch (error) {
      console.error('Error creating lead address:', error);
      notificationModal('Error creating address. Please try again.', false, true);
    }
  };

  // Proceed to the next step
  const nextStep = () => { setStep(step + 1);};

  // Return to the previous step
  const prevStep = () => setStep(step - 1);
  // Submit estimate, either as save, generate PDF, or send

  const [createdEstimateId, setCreatedEstimateId] = useState(null);

  const notificationModal = (message, success, open ) => {
    setFeedbackSuccess(success);
    setFeedbackMessage(message);
    setShowFeedback(open);
  }

  const handleFeedbackComplete = () => {
    setShowFeedback(false);
    if (feedbackSuccess) {
      if (createdEstimateId) {
        navigate(`/estimates/edit/${createdEstimateId}`, { replace: true });
      } else {
        navigate('/estimates');
      }
    }
  };

  const handleSubmit = async (action) => {
    if (discountCalc.error) {
      notificationModal(discountCalc.error, false, true);
      return;
    }
    try {
      setLoading(true); // Show loading state while the request is being sent

      console.log(formData);
      // Prepare lead information
      let leadInfo = {};
      let response;
        if (formData.lead.id) {
          leadInfo.leadId = formData.lead.id;
        } else {
          leadInfo.leadData = {
            name: formData.lead.name,
            email: formData.lead.email,
            phone: formData.lead.phone,
            street: formData.lead.street,
            city: formData.lead.city,
            state: formData.lead.state,
            zipcode: formData.lead.zipcode,
            accept_sms: formData.lead.accept_sms,
            when_ready: formData.lead.when_ready,
            job_observation: formData.lead.job_observation, // Include the job observation field
            origin_name: formData.lead.lead_origin,
          };
        }

      // Prepare estimate items
      const items = formData.services.map((service) => ({
        serviceId: service.serviceId,
        totalArea: service.totalArea,
        pricePerSqft: service.pricePerSqft,
        totalPrice: service.totalPrice,
        measurementUnit: service.measurementUnit || 'sqft',
        observation: service.observation || '',
        areaMeasurements: service.areaMeasurements || [],
      }));

      // Prepare estimate details
      console.log(formData.EstimateDetailId);
      const details = formData.EstimateDetailId.map((detail) => (detail.id));
      const EstimateDetailId = details;
      console.log("Details gett", details);

      // Prepare general estimate information
      const estimateInfo = {
        // The API recomputes subtotal/totalPrice from the items and discount
        totalPrice: discountedTotal,
        ...discountPayload(discountType, discountValue),
        discountReason: discountMotive || null,
        status: action === 'send' ? 'Sent' : 'Draft', // Change status based on the action
        // Quote validity — keep in step with ESTIMATE_VALIDITY_DAYS in the
        // backend's helpers/estimateHelpers.js (the API re-defaults anyway if
        // this is missing or already past).
        validUntil: formData.estimateData.validUntil || new Date(Date.now() + ESTIMATE_VALIDITY_DAYS * 24 * 60 * 60 * 1000).toISOString(),
        jobSiteDistance: suppliers.length > 0 ? suppliers[0].distance : 0, // Assume the distance to the nearest supplier is jobSiteDistance
        notes: formData.notes || '',
        introduction: formData.introduction || '',
        serviceRequestIds: serviceRequestId,
      };

      // Combine all parts into the final request body
      const requestBody = {
        ...leadInfo,
        leadAddressId: addressMode === 'different' && selectedAddressId ? selectedAddressId : null,
        items,
        details,
        EstimateDetailId,
        ...estimateInfo,
      };

      console.log("Dados sendo enviados: ", requestBody);

      if(estimateId){
        response = await updateEstimate(estimateId, requestBody);
      }else{
        // Send the request
        response = await createEstimate(requestBody);
      }

      const mensagem = estimateId ? 'updat' : 'creat';
      // Handle success response
      if (response.status === 201 || response.status === 200) {
        // For new estimates, capture the ID so we redirect to edit mode
        if (!estimateId) {
          const newId = response.data?.id || response.data?.estimate?.id;
          if (newId) setCreatedEstimateId(newId);
        }
        notificationModal(`Estimate ${mensagem}ed successfully`, true, true);
      } else {
        notificationModal(`Something went wrong ${mensagem}ing Estimate`, false, true);

      }
    } catch (error) {
      const mensagem = estimateId ? 'updating' : 'creating';
      const apiMessage = error.response?.data?.message || error.response?.data?.error;
      notificationModal(`Error ${mensagem} the estimate. Error: ${apiMessage || error.message}`, false, true);
      console.error('Error creating estimate:', error);
      console.log(error);
    } finally {
      setLoading(false); // Hide loading state after the request is complete
    }
  };

    const selectedDetailNames = availableDetails
  .filter((detail) => Array.isArray(formData.EstimateDetailId) && formData.EstimateDetailId.some(item => item.id === detail.id)) //formData.EstimateDetailId.includes(detail.id))
  .map((detail) => detail.name);

  // AI Generate: Open prompt modal
  const openAiPromptModal = (type) => {
    if (!formData.services || formData.services.length === 0) {
      setFeedbackMessage(`Please add at least one service before generating ${type === 'introduction' ? 'a description' : 'footnotes'}.`);
      setFeedbackSuccess(false);
      setShowFeedback(true);
      return;
    }
    setAiCustomInstructions('');
    setAiPromptModal({ open: true, type });
  };

  // AI Generate: Execute generation with custom instructions
  const handleAiGenerate = async () => {
    const { type } = aiPromptModal;
    setAiPromptModal({ open: false, type: null });

    const items = formData.services.map(s => ({
      serviceName: s.serviceName || s.name,
      totalArea: s.totalArea,
      pricePerSqft: s.pricePerSqft,
      totalPrice: s.totalPrice,
      measurementUnit: s.measurementUnit || 'sqft',
      observation: s.observation
    }));
    const instructions = aiCustomInstructions.trim() || undefined;

    if (type === 'introduction') {
      setGeneratingIntro(true);
      try {
        const result = await aiGenerateIntroduction(items, discountedTotal || totalValue, instructions);
        setFormData({ ...formData, introduction: result.introduction });
      } catch (err) {
        setFeedbackMessage('Error generating description. Please try again.');
        setFeedbackSuccess(false);
        setShowFeedback(true);
      }
      setGeneratingIntro(false);
    } else {
      setGeneratingFootnotes(true);
      try {
        const result = await aiGenerateFootnotes(items, discountedTotal || totalValue, instructions);
        setFormData({ ...formData, notes: result.footnotes });
      } catch (err) {
        setFeedbackMessage('Error generating footnotes. Please try again.');
        setFeedbackSuccess(false);
        setShowFeedback(true);
      }
      setGeneratingFootnotes(false);
    }
  };

  // Step-by-step rendering
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
          <FormSection>
            <h3>Lead Details</h3>
            <FormGrid>
              <FormField index={0} style={{ position: 'relative', zIndex: 10 }}>
              <Autocomplete
                icon={<FaSearch />}
                value={formData.lead.name}
                onChange={(value) => handleLeadChange({ target: { name: 'name', value } })}
                suggestions={availableLeads}
                onSelect={handleLeadSelect}
                placeholder="Enter lead's full name or select from suggestions"
                onSearch={handleLeadNameSearch}
              />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField index={1}>
              <Input
              icon={<FaUser />}
              label="Full Name"
              name="name"
              value={formData.lead.name}
              onChange={handleLeadChange}
              placeholder="Enter lead's full name"
              required
              disabled={leadFieldsDisabled}
            />
              </FormField>
              </FormGrid>
              <FormGrid>
              <FormField index={2}>
              <GooglePlaceAutocomplete
              initialValue={formData.lead.street}
              placeholder="Enter address"
              onSelect={handleAddressSelect}
              required
              disabled={leadFieldsDisabled}
            />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField index={3}>
              <Input
              icon={<FaEnvelope />}
              label="Email"
              name="email"
              value={formData.lead.email}
              onChange={handleLeadChange}
              placeholder="Enter lead's email"
              required
              disabled={leadFieldsDisabled}
            />
              </FormField>
              <FormField index={4}>
              <MaskedInput
              icon={<FaPhone />}
              label="Phone"
              name="phone"
              value={formData.lead.phone}
              onChange={handleLeadChange}
              placeholder="Enter lead's phone number"
              mask={`+19999999999`}
              required
              disabled={leadFieldsDisabled}
            />
              </FormField>
            </FormGrid>
            </FormSection>

            {/* Address Selection - only show when existing lead is selected */}
            {leadFieldsDisabled && formData.lead.id && (
              <FormSection>
                <h3><FaMapMarkerAlt /> Quote Address</h3>
                <AddressOptionList>
                  <AddressOption $active={addressMode === 'main'} onClick={() => { setAddressMode('main'); setSelectedAddressId(null); setNewAddressMode(false); }}>
                    <input type="radio" name="addressMode" checked={addressMode === 'main'} readOnly />
                    <div className="addr-content">
                      <span className="addr-label">Main Address</span>
                      <span className="addr-detail">{[formData.lead.street, formData.lead.city, formData.lead.state, formData.lead.zipcode].filter(Boolean).join(', ')}</span>
                    </div>
                  </AddressOption>

                  <AddressOption $active={addressMode === 'different'} onClick={() => setAddressMode('different')}>
                    <input type="radio" name="addressMode" checked={addressMode === 'different'} readOnly />
                    <span className="addr-label">Different Address</span>
                  </AddressOption>

                  {addressMode === 'different' && (
                    <div style={{ paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {leadAddresses.map((addr) => (
                        <AddressOption key={addr.id} $active={selectedAddressId === addr.id} $nested onClick={() => { setSelectedAddressId(addr.id); setNewAddressMode(false); }}>
                          <input type="radio" name="selectedAddress" checked={selectedAddressId === addr.id} readOnly />
                          <div className="addr-content">
                            {addr.label && <span className="addr-label">{addr.label}</span>}
                            <span className="addr-detail">{[addr.street, addr.city, addr.state, addr.zipcode].filter(Boolean).join(', ')}</span>
                          </div>
                        </AddressOption>
                      ))}

                      {!newAddressMode ? (
                        <AddNewAddrButton type="button" onClick={() => { setNewAddressMode(true); setSelectedAddressId(null); }}>
                          <FaPlus /> Add New Address
                        </AddNewAddrButton>
                      ) : (
                        <NewAddressForm>
                          <Input
                            label="Label (optional)"
                            name="label"
                            value={newAddress.label}
                            onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                            placeholder="e.g. Building A, Property #2"
                          />
                          <GooglePlaceAutocomplete
                            initialValue={newAddress.street}
                            placeholder="Enter new address"
                            onSelect={handleNewAddressSelect}
                          />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button type="button" onClick={handleSaveNewAddress} disabled={!newAddress.street}>
                              Save Address
                            </Button>
                            <Button type="button" variant="secondary" onClick={() => { setNewAddressMode(false); setNewAddress({ label: '', street: '', city: '', state: '', zipcode: '', latitude: null, longitude: null }); }}>
                              Cancel
                            </Button>
                          </div>
                        </NewAddressForm>
                      )}
                    </div>
                  )}
                </AddressOptionList>
              </FormSection>
            )}

            <FormSection>
              <h3>Extra</h3>
              <FormGrid>
                <FormField index={6}>
                <Select
              icon={<FaCalendar />}
              label="When Ready"
              name="when_ready"
              value={formData.lead.when_ready}
              onChange={handleLeadChange}
              disabled={leadFieldsDisabled}
            >
              <option value="">Select readiness</option>
              <option value="ASAP">ASAP</option>
              <option value="In a Month">In a Month</option>
              <option value="In two Months">In two Months</option>
              <option value="Just Looking for Prices">Just Looking for Prices</option>
            </Select>
                </FormField>
                <FormField index={7}>
                <Select
                icon={<FaArrowDown />}
                label="Lead Origin"
                name="lead_origin"
                value={formData.lead.lead_origin}
                disabled={leadFieldsDisabled}
                onChange={handleLeadChange}
                >
                <option value="">Select Lead Origin</option>
                {leadOrigins.map((origin) => (
                    <option key={origin.id} value={origin.origin_name}>
                    {origin.origin_name}
                </option>
            ))}
            </Select>
                </FormField>
              </FormGrid>
              <FormGrid>
                <FormField index={8}>
                <Input
                icon={<FaTextHeight />}
                disabled={leadFieldsDisabled}
                label="Job Observation"
                name="job_observation"
                value={formData.lead.job_observation}
                onChange={handleLeadChange}
                placeholder="Enter job-related observations"
            />
                </FormField>
              </FormGrid>
              <FormGrid>
                <FormField index={9}>
                <Checkbox
              label="Accept SMS"
              disabled={leadFieldsDisabled}
              checked={formData.lead.accept_sms}
              onChange={() => setFormData({
                ...formData,
                lead: {
                  ...formData.lead,
                  accept_sms: !formData.lead.accept_sms
                }
              })}
            />
                </FormField>
              </FormGrid>
            </FormSection>            
          </>
        );
      case 2:
            return (
              <>
                <FormSection>
                  <h3>Services</h3>                 
                    <ServiceItem key={"index"}>
                        <div>
                            {formData.services.length > 0 ? (
                              console.log("Services: ",formData.services),
                                formData.services.map((service, index) => (
                                <ServiceItem key={index}>
                                <div className="header">
                                    <span className='serviceName'>{service.serviceName}</span>
                                    <div>
                                        <ActionIcons>
                                        {/* Add actions to send SMS, call, view, edit, and delete */}
                                            <FaEdit onClick={() => handleServiceEdit(index)} />
                                            <FaTrash onClick={() => handleServiceDelete(index)} />
                                        </ActionIcons>
                                    </div>
                                </div>
                                <div className="content">
                                    <span>Price: {formatCurrency(service.pricePerSqft)}/{getUnitInfo(service.measurementUnit).short}</span>
                                    <span>Area: {service.totalArea} {getUnitInfo(service.measurementUnit).short}</span>
                                    <span>Subtotal: {formatCurrency(service.totalPrice)}</span>
                                </div>
                                </ServiceItem>
                            ))) : (

                                <div>
                                  <center>
                                  <FaRegHdd /><br />
                                  No Services yet.</center></div>
                              )
                            }
                        {/* Discount Section */}
                        <DiscountSection>
                            <DiscountLabel>Discount</DiscountLabel>
                            <DiscountInputWrapper>
                            <DiscountInput
                                type={discountType}
                                value={discountValue}
                                onTypeChange={handleDiscountTypeChange}
                                onValueChange={setDiscountValue}
                                error={discountCalc.error}
                            />
                            <MaskedInput
                                label="Discount Motive"
                                value={discountMotive}
                                onChange={handleDiscountMotiveChange}
                                placeholder="Reason for discount"
                            />
                            </DiscountInputWrapper>
                            <div className="valueTotal">
                            <span>Subtotal: {formatCurrency(totalValue)}</span><br />
                            <span>Discount Applied: {formatCurrency(discountCalc.amount)}</span>
                            </div>
                        </DiscountSection>
                        <div className='valueTotal'>Total: {formatCurrency(discountedTotal)}</div>
                    </div>
                    </ServiceItem>
                    <center><ActionButton onClick={handleAddService}>
                    <FaPlus /> Add Service
                  </ActionButton></center>
                </FormSection>
                <FormSection>
                  <h3>Service Details</h3>
                      <FormField index={11}>
                      <ServiceItem key={"index"}>
                    {availableDetails.map((detail) => (
                    <Checkbox
                        key={detail.id}
                        label={detail.name}
                        checked={formData.EstimateDetailId.some(item => item.id === detail.id)}
                        onChange={() => handleDetailChange(detail.id)}
                    />
                    ))}
                    </ServiceItem>
                      </FormField>
                </FormSection>

                <FormSection>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Estimate Description</h3>
                    <ActionButton onClick={() => openAiPromptModal('introduction')} disabled={generatingIntro} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
                      <FaMagic /> {generatingIntro ? 'Generating...' : 'AI Generate'}
                    </ActionButton>
                  </div>
                  <FormField>
                    <TextArea
                    label="Description"
                    value={formData.introduction || ''}
                    onChange={(e) => setFormData({...formData, introduction: e.target.value})}
                    placeholder="Enter an introduction for this estimate..."
                    />
                  </FormField>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Notes</h3>
                    <ActionButton onClick={() => openAiPromptModal('footnotes')} disabled={generatingFootnotes} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
                      <FaMagic /> {generatingFootnotes ? 'Generating...' : 'AI Generate'}
                    </ActionButton>
                  </div>
                  <FormField>
                    <TextArea
                    label="Notes"
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Enter any extra notes to be in the estimate..."
                    />
                  </FormField>

                </FormSection>

                <FormSection>
                  <h3><FaCloudUploadAlt /> Estimate Images</h3>
                  <EstimateImageUpload estimateId={estimateId} />
                </FormSection>

                <FormSection>
                  <AccordionHeader onClick={() => setShowSuppliers(!showSuppliers)}>
                    <h3><FaMap /> Suppliers Nearby</h3>
                    {showSuppliers ? <FaChevronDown /> : <FaChevronRight />}
                  </AccordionHeader>
                  <AccordionContent $isOpen={showSuppliers}>
                    <MapWrapper>
                      {effectiveCoords.lat && effectiveCoords.lng && (
                          <Map
                            addresses={[
                                {
                                lat: effectiveCoords.lat,
                                lng: effectiveCoords.lng,
                                title: 'Job Site',
                                isSupplier: false,
                                },
                                ...formattedSuppliers,
                            ]}
                            jobSitePin={jobSitePinUrl}
                            supplierPin={supplierPinUrl}
                            />
                        )}
                    </MapWrapper>
                    <SupplierListComponent suppliers={suppliers} />
                  </AccordionContent>
                </FormSection>
              </>
            );
      case 3:
        return (
            <>
            <FormSection>
              <SummaryTitle><FaUser/> Lead Information</SummaryTitle>
              <DetailGrid>
              <DetailField>
                <label>Name</label>
                <span>{formData.lead.name}</span>
              </DetailField>
              <DetailField>
                <label>Quote Address</label>
                <span>{[getEffectiveAddress().street, getEffectiveAddress().city, getEffectiveAddress().state, getEffectiveAddress().zipcode].filter(Boolean).join(', ')}</span>
                {addressMode === 'different' && selectedAddressId && (() => {
                  const addr = leadAddresses.find(a => a.id === selectedAddressId);
                  return addr?.label ? <span style={{ fontSize: '0.85em', color: '#F69620' }}>{addr.label}</span> : null;
                })()}
              </DetailField>
              <DetailField>
                <label>Email</label>
                <span>{formData.lead.email}</span>
              </DetailField>
              <DetailField>
                <label>Phone</label>
                <span>{formData.lead.phone}</span>
              </DetailField>
              <DetailField>
                <label>Origin</label>
                <span>{formData.lead.lead_origin}</span>
              </DetailField>
              <DetailField>
                <label>When Ready?</label>
                <span>{formData.lead.when_ready}</span>
              </DetailField>
              {formData.lead.job_observation && (
              <DetailField>
                  <label>Notes</label>
                  <span>{formData.lead.job_observation}</span>
                </DetailField>
              )}
              </DetailGrid>
            </FormSection>
              
              <SummaryWrapper>
              <SummaryTitle><FaTools/> Services Overview</SummaryTitle>
              {formData.services.map((service, index) => (
              <DetailGridSeparator>
                <DetailField>
                  <label>Service</label>
                  <span>{service.serviceName}</span>
                </DetailField>
                <DetailField>
                  <label>Area</label>
                  <span>{service.totalArea} {getUnitInfo(service.measurementUnit).short}</span>
                </DetailField>
                <DetailField>
                  <label>Price / {getUnitInfo(service.measurementUnit).short}</label>
                  <span>{formatCurrency(service.pricePerSqft)}</span>
                </DetailField>
                <DetailField>
                  <label>Subtotal</label>
                  <span>{formatCurrency(service.totalPrice)}</span>
                </DetailField>
              </DetailGridSeparator>
              ))} 
              
              {discountCalc.amount > 0 && (
                <SummaryTitle>
                  - Discount: {formatCurrency(discountCalc.amount)}
                  {discountType === 'percentage' ? ` (${discountValue}%)` : ''}
                  {discountMotive ? ` · ${discountMotive}` : ''}
                </SummaryTitle>
              )}
              <SummaryTitle>= Total: {formatCurrency(discountCalc.total)} </SummaryTitle>
              </SummaryWrapper>


              <>
              {selectedDetailNames.length > 0 ? (
                <SummaryWrapper>
                <SummaryTitle><FaTractor /> Job Details</SummaryTitle>
                <SummaryContent>
                  
                    <>
                    {selectedDetailNames.map((name, index) => (
                    <DetailField>
                       <FaRegArrowAltCircleRight /> {name}
                    </DetailField>
                    ))}
                    </>
                </SummaryContent>
              </SummaryWrapper>
              ) : (
                <div></div>
              )}
              </>

              {/* Supplier Information Section - collapsible */}
              <SupplierListWrapper>
                <AccordionHeader onClick={() => setShowSuppliers(!showSuppliers)}>
                  <h3><FaMap /> Suppliers Around</h3>
                  {showSuppliers ? <FaChevronDown /> : <FaChevronRight />}
                </AccordionHeader>
                <AccordionContent $isOpen={showSuppliers}>
                  <SupplierListComponent suppliers={suppliers} />
                </AccordionContent>
              </SupplierListWrapper>
            </>
          );
      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      {loading && <LoadingSpinner />}
      {!loading && (
      <FormContainer>
        <FormHeader>
          <BackButton onClick={() => navigate('/estimates')}>
            <FaArrowLeft /> Back to Estimates
          </BackButton>
          <h2>{estimateId ? 'Edit Estimate' : 'New Estimate'}</h2>
        </FormHeader>
          <Stepper currentStep={step} totalSteps={3} />
          {renderStepContent()}
          <NavigationButtons>
              {step > 1 && <Button type="button" onClick={prevStep}><FaArrowLeft /></Button>}
              {step < 3 ? (
                <Button type="button" onClick={nextStep}><FaArrowRight /></Button>
              ) : (
                <>
                <Button type="button" onClick={() => {navigate('/estimates')}}><FaTimes /> Cancel</Button>
                <Button type="button" onClick={handleSubmit}><FaCheck /> {estimateId ? 'Update' : 'Submit'}</Button>
                </>
              )}
            </NavigationButtons>
      </FormContainer>
        )}
        {serviceModalOpen && (
          <ServiceModal
            isOpen={serviceModalOpen}
            onClose={() => setServiceModalOpen(false)}
            onCancel={() => setServiceModalOpen(false)}
            onSave={handleServiceSave}
            availableServices={availableServices}
            existingService={selectedServiceIndex !== null ? formData.services[selectedServiceIndex] : null}
          />
        )}

      {aiPromptModal.open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div style={{
            background: '#1e1e2f', padding: '24px', borderRadius: '8px',
            width: '500px', maxWidth: '90%', boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaMagic /> {aiPromptModal.type === 'introduction' ? 'Generate Description' : 'Generate Footnotes'}
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#aaa', marginBottom: '16px' }}>
              Add any custom instructions to personalize the AI output. Leave empty to use the default prompt.
            </p>
            <textarea
              value={aiCustomInstructions}
              onChange={(e) => setAiCustomInstructions(e.target.value)}
              placeholder={aiPromptModal.type === 'introduction'
                ? 'e.g. "The Others item is for concrete curbing. Use a warm and friendly tone."'
                : 'e.g. "Mention we do not cover patch or mold. Add a 2-year warranty note."'}
              style={{
                width: '100%', minHeight: '100px', padding: '10px 12px', borderRadius: '6px',
                border: '1px solid #333', background: '#141422', color: '#fff',
                fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
              <button
                onClick={() => setAiPromptModal({ open: false, type: null })}
                style={{
                  padding: '10px 20px', borderRadius: '6px', border: 'none',
                  background: '#e74c3c', color: '#fff', cursor: 'pointer', fontSize: '0.9rem'
                }}
              >Cancel</button>
              <button
                onClick={handleAiGenerate}
                style={{
                  padding: '10px 20px', borderRadius: '6px', border: 'none',
                  background: '#3498db', color: '#fff', cursor: 'pointer', fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', gap: '6px'
                }}
              ><FaMagic /> Generate</button>
            </div>
          </div>
        </div>
      )}

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={handleFeedbackComplete}
      />


    </DashboardLayout>


  );
};

export default EstimateForm;